import React, { createContext, useContext, useState, useEffect } from 'react';
import { developerService } from '../services/developerService';

interface DeveloperContextType {
  isDeveloperAuthenticated: boolean;
  login: (password: string) => Promise<boolean>;
  logout: () => void;
  setDeveloperPassword: (password: string) => Promise<void>;
  hasPassword: boolean;
}

const DeveloperContext = createContext<DeveloperContextType | undefined>(undefined);

export const useDeveloper = () => {
  const context = useContext(DeveloperContext);
  if (!context) {
    throw new Error('useDeveloper must be used within a DeveloperProvider');
  }
  return context;
};

export const DeveloperProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDeveloperAuthenticated, setIsDeveloperAuthenticated] = useState(false);
  const [hasPassword, setHasPassword] = useState(false);

  // Verificar se já existe senha salva
  useEffect(() => {
    const checkPassword = async () => {
      try {
        const exists = await developerService.hasPassword();
        setHasPassword(exists);
      } catch (error) {
        console.error('Erro ao verificar senha de desenvolvedor:', error);
        setHasPassword(false);
      }
    };
    
    checkPassword();
  }, []);

  const login = async (password: string): Promise<boolean> => {
    try {
      const isValid = await developerService.verifyPassword(password);
      if (isValid) {
        setIsDeveloperAuthenticated(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erro ao fazer login de desenvolvedor:', error);
      return false;
    }
  };

  const logout = () => {
    setIsDeveloperAuthenticated(false);
  };

  const setDeveloperPassword = async (password: string): Promise<void> => {
    try {
      await developerService.setPassword(password);
      setHasPassword(true);
    } catch (error) {
      console.error('Erro ao definir senha de desenvolvedor:', error);
      throw error;
    }
  };

  return (
    <DeveloperContext.Provider
      value={{
        isDeveloperAuthenticated,
        login,
        logout,
        setDeveloperPassword,
        hasPassword
      }}
    >
      {children}
    </DeveloperContext.Provider>
  );
};
